import { useCallback, useEffect, useRef, useState } from 'react';

interface SearchableItem {
  id: string;
  name: string;
}

interface UseSearchableOptionsResult<T> {
  options: T[];
  loading: boolean;
  error: string | null;
  setSearch: (value: string) => void;
  labelFor: (id: string) => string;
}

// Backs the clients / outlets / hubs dropdowns. Initial open fires an
// un-narrowed fetch; keystrokes debounce at 250ms before re-fetching with
// `?search=`. The label cache lets the dropdown still show the selected
// option's name after a search narrows it out of the result set.
export function useSearchableOptions<T extends SearchableItem>(
  open: boolean,
  fetcher: (search?: string) => Promise<T[]>
): UseSearchableOptionsResult<T> {
  const [options, setOptions] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const labelCache = useRef<Map<string, string>>(new Map());

  useEffect(() => {
    if (!open) {
      setSearch('');
      return;
    }

    const trimmed = search.trim();
    let cancelled = false;

    const fire = () => {
      setLoading(true);
      fetcher(trimmed || undefined)
        .then((items) => {
          if (cancelled) return;
          setOptions(items);
          setError(null);
          for (const it of items) labelCache.current.set(it.id, it.name);
        })
        .catch((e) => {
          if (cancelled) return;
          setError(e instanceof Error ? e.message : 'Failed to load options');
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    };

    let timer: ReturnType<typeof setTimeout> | undefined;
    if (trimmed) {
      timer = setTimeout(fire, 250);
    } else {
      fire();
    }

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [open, search, fetcher]);

  const labelFor = useCallback(
    (id: string) => labelCache.current.get(id) ?? '',
    []
  );

  return { options, loading, error, setSearch, labelFor };
}
